import Grid from '@mui/material/Grid';
import MuiTextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useState } from 'react';
import { Field, useFormState } from 'react-final-form';

import ConsultationDates from '@/components/ConsultationDates';
import DurationPicker from '@/components/DurationPicker';
import LegalNotes from '@/components/RequestConsultation/LegalNotes';

import PriceSummary from './PriceSummary';
import type { SchedulingFormProps } from './types';

const useStyles = makeStyles(() => ({
  container: {
    padding: '0 24px',
    // Consistent 16px label sizing for all form fields
    '& .MuiInputLabel-root': {
      fontSize: 16,
    },
    // Remove scale transform on shrink so label stays 16px
    '& .MuiInputLabel-shrink': {
      transform: 'translate(0, -1.5px) scale(1)',
    },
    // Add space between DurationPicker label and dropdown
    '& .MuiFormControl-root .MuiSelect-select': {
      marginTop: 12,
    },
  },
  field: {
    marginTop: 4,
  },
}));

/**
 * Shared scheduling form component for consultation requests.
 * Supports both dollar pricing (SMB customers) and credit pricing (credit customers).
 *
 * Usage:
 * - SMB Modal: <SchedulingForm pricing={{ type: 'dollars', hourlyRate: 250 }} ... />
 * - Credits: <SchedulingForm pricing={{ type: 'credits', creditRate: 0.83 }} ... />
 */
export function SchedulingForm({
  recipientName,
  recipientTimezone,
  viewer,
  pricing,
  bookingFeeCents,
  descriptionLabel,
  descriptionMinLength = 0,
  showTimezoneLink = true,
  showLegalNotes = false,
  submitButtonLabel,
  children,
}: SchedulingFormProps): JSX.Element {
  const s = useStyles();
  const { values } = useFormState({ subscription: { values: true } });
  const [charCount, setCharCount] = useState(0);

  // Initialize char count from form values (for when navigating back)
  useEffect(() => {
    const initialLength = values?.description?.trim()?.length || 0;
    if (initialLength > 0) {
      setCharCount(initialLength);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get first name for display
  const firstName = recipientName.split(' ')[0];

  // Character count for visual feedback
  const hasMinChars = descriptionMinLength === 0 || charCount >= descriptionMinLength;
  const charsRemaining = descriptionMinLength - charCount;

  // Parse duration from form value (e.g., "30m", "1h", "1.5h") to minutes
  const getDurationMinutes = (durationStr?: string): number => {
    if (!durationStr) return 60; // default

    if (durationStr.endsWith('m')) {
      return parseInt(durationStr, 10);
    }
    if (durationStr.endsWith('h')) {
      return parseFloat(durationStr) * 60;
    }
    return 60;
  };

  const durationMinutes = getDurationMinutes(values?.duration);

  // Determine DurationPicker props based on pricing type
  const durationPickerProps =
    pricing.type === 'dollars'
      ? { hourlyRate: pricing.hourlyRate, showDollars: true }
      : { creditRate: pricing.creditRate, showCredits: !pricing.isFixed };

  return (
    <div className={s.container}>
      <Grid container justifyContent="space-between">
        {/* Note to expert */}
        <Grid item xs={12}>
          <Field name="description">
            {({ input }) => (
              <MuiTextField
                {...input}
                multiline
                fullWidth
                label={descriptionLabel || `Include a note to ${firstName}`}
                onChange={(e) => {
                  input.onChange(e);
                  setCharCount(e.target.value.trim().length);
                }}
                helperText={
                  descriptionMinLength > 0 && !hasMinChars
                    ? `Minimum length: ${charsRemaining} more character${charsRemaining !== 1 ? 's' : ''}`
                    : undefined
                }
                FormHelperTextProps={{
                  style: { color: charCount > 0 && !hasMinChars ? '#d32f2f' : undefined },
                }}
                InputLabelProps={{
                  style: { fontSize: 16 },
                }}
                InputProps={{
                  style: { paddingTop: 8 },
                }}
              />
            )}
          </Field>
        </Grid>

        {/* Duration picker */}
        <Grid item xs={12} className={s.field}>
          <Field
            component={DurationPicker}
            name="duration"
            labelProps={{ style: { fontSize: 16, marginBottom: 8 } }}
            {...durationPickerProps}
          />
        </Grid>

        {/* Consultation dates */}
        <Grid item xs={12}>
          <ConsultationDates
            name="dates"
            viewer={viewer}
            userName={firstName}
            userTimezone={recipientTimezone}
            label="Suggest times that work for you"
            style={{ marginTop: 12 }}
            hideTimezoneLink={!showTimezoneLink}
          />
        </Grid>

        {/* Additional fields slot (for logged-in users) */}
        {children}
      </Grid>

      {/* Price summary */}
      <PriceSummary
        duration={durationMinutes}
        pricing={pricing}
        showChargeNote={true}
        bookingFeeCents={bookingFeeCents}
      />

      {/* Legal notes (optional) */}
      {showLegalNotes && <LegalNotes buttonText={submitButtonLabel} />}
    </div>
  );
}

export default SchedulingForm;
